import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import {
  HiPlus as Add,
  HiTrash as Delete,
  HiDownload as Download,
  HiPencil as Edit,
  HiChevronDown as ExpandMore,
  HiUpload as Upload,
} from 'react-icons/hi';
import { mockData } from './MockData';

const CurriculumPlanning: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBandDialog, setShowBandDialog] = useState(false);
  const [curriculumPlan, setCurriculumPlan] = useState(mockData.curriculumPlan);
  const [yearGroups, setYearGroups] = useState(mockData.yearGroups);
  const [newSubject, setNewSubject] = useState<any>({});
  const [newBand, setNewBand] = useState<any>({});

  const steps = [
    'Import/Create Curriculum Plan',
    'Assign Subjects to Year Groups',
    'Create Year Band Groups',
    'Configure Subject Sets',
    'Review and Validate',
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleAddSubject = () => {
    const subjectWithId = { ...newSubject, id: Date.now().toString() };
    setCurriculumPlan({
      ...curriculumPlan,
      subjects: [...curriculumPlan.subjects, subjectWithId],
    });
    setNewSubject({});
    setShowAddDialog(false);
  };

  const handleAddBand = () => {
    const bandWithId = { ...newBand, id: Date.now().toString() };
    setYearGroups(
      yearGroups.map((yg) => (yg.id === newBand.yearGroupId ? { ...yg, bands: [...yg.bands, bandWithId] } : yg)),
    );
    setNewBand({});
    setShowBandDialog(false);
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Import Curriculum Plan
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Upload a structured file (CSV, Excel) or create manually
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Button variant="outlined" startIcon={<Download />} sx={{ mr: 1, mb: 1 }}>
                      Download Template
                    </Button>
                    <Button variant="outlined" startIcon={<Upload />} sx={{ mb: 1 }}>
                      Upload File
                    </Button>
                  </Box>
                  <Paper
                    sx={{
                      p: 3,
                      border: '2px dashed',
                      borderColor: 'grey.300',
                      bgcolor: 'grey.100',
                      textAlign: 'center',
                      minHeight: 100,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Upload style={{ fontSize: 40, color: '#9e9e9e', marginBottom: 8 }} />
                    <Typography variant="body2">Drag & drop curriculum file here or click to browse</Typography>
                  </Paper>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Current Curriculum Plan
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Chip label={`${curriculumPlan.subjects.length} Subjects`} color="primary" sx={{ mr: 1 }} />
                    <Chip label={curriculumPlan.cycleType} color="secondary" />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Academic Year: {curriculumPlan.academicYear}
                  </Typography>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Curriculum plan loaded successfully with {curriculumPlan.subjects.length} subjects
                    </Typography>
                  </Alert>
                  <Button variant="contained" startIcon={<Add />} onClick={() => setShowAddDialog(true)}>
                    Add Subject Manually
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Subject-Year Group Allocation Matrix
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Subject</TableCell>
                          <TableCell>Department</TableCell>
                          <TableCell>Year 7</TableCell>
                          <TableCell>Year 8</TableCell>
                          <TableCell>Year 9</TableCell>
                          <TableCell>Year 10</TableCell>
                          <TableCell>Year 11</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {curriculumPlan.subjects.map((subject) => (
                          <TableRow key={subject.id}>
                            <TableCell>{subject.subjectName}</TableCell>
                            <TableCell>{subject.department || 'N/A'}</TableCell>
                            {['Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11'].map((year) => (
                              <TableCell key={year}>
                                {subject.yearGroups.find((yg) => yg.yearGroup === year) ? (
                                  <Chip
                                    label={`${subject.yearGroups.find((yg) => yg.yearGroup === year)?.periodsPerWeek || 0}/week`}
                                    color="primary"
                                    size="small"
                                  />
                                ) : (
                                  <Chip label="Not assigned" variant="outlined" size="small" />
                                )}
                              </TableCell>
                            ))}
                            <TableCell>
                              <IconButton size="small" color="primary">
                                <Edit />
                              </IconButton>
                              <IconButton size="small" color="error">
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Year Band Groups Configuration
                  </Typography>
                  {yearGroups.map((yearGroup) => (
                    <Accordion key={yearGroup.id} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle1">
                          {yearGroup.name} - {yearGroup.bands.length} Bands
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {yearGroup.bands.map((band) => (
                            <Grid item xs={12} sm={6} md={4} key={band.id}>
                              <Card variant="outlined">
                                <CardContent>
                                  <Typography variant="subtitle2">{band.name}</Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Type: {band.type}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Capacity: {band.capacity}
                                  </Typography>
                                  <Box sx={{ mt: 1 }}>
                                    <IconButton size="small" color="primary">
                                      <Edit />
                                    </IconButton>
                                    <IconButton size="small" color="error">
                                      <Delete />
                                    </IconButton>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                          <Grid item xs={12} sm={6} md={4}>
                            <Card variant="outlined" sx={{ borderStyle: 'dashed' }}>
                              <CardContent sx={{ textAlign: 'center' }}>
                                <Button
                                  startIcon={<Add />}
                                  onClick={() => {
                                    setNewBand({ yearGroupId: yearGroup.id });
                                    setShowBandDialog(true);
                                  }}
                                >
                                  Add Band
                                </Button>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Band Configuration Tips
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Bands are used to group students by ability or for organizational purposes.
                    </Typography>
                  </Alert>
                  <Typography variant="subtitle2" gutterBottom>
                    Band Types:
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Chip label="Ability-based" color="primary" size="small" sx={{ mr: 1, mb: 1 }} />
                    <Chip label="Mixed ability" color="secondary" size="small" sx={{ mr: 1, mb: 1 }} />
                    <Chip label="Non-ability" color="default" size="small" sx={{ mb: 1 }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Configure bands to reflect your school's teaching strategies and student grouping preferences.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Subject Sets Configuration
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Create ability-based sets within bands for specific subjects
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Subject</TableCell>
                          <TableCell>Year Group</TableCell>
                          <TableCell>Band</TableCell>
                          <TableCell>Sets</TableCell>
                          <TableCell>Parallel Teaching</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mockData.subjectSets.map((set) => (
                          <TableRow key={set.id}>
                            <TableCell>{set.subjectName}</TableCell>
                            <TableCell>{set.yearGroup}</TableCell>
                            <TableCell>{set.bandName}</TableCell>
                            <TableCell>
                              <Chip label={`${set.setCount} sets`} color="primary" size="small" />
                            </TableCell>
                            <TableCell>
                              <Switch checked={set.parallelTeaching} size="small" />
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" color="primary">
                                <Edit />
                              </IconButton>
                              <IconButton size="small" color="error">
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      case 4:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Curriculum Plan Validation
                  </Typography>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2">✓ All core subjects assigned to required year groups</Typography>
                  </Alert>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2">✓ Teaching loads balanced across year groups</Typography>
                  </Alert>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">⚠ Year 10 has high lesson density on Tuesdays</Typography>
                  </Alert>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Summary Statistics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">
                          {curriculumPlan.subjects.length}
                        </Typography>
                        <Typography variant="body2">Subjects</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">
                          {yearGroups.length}
                        </Typography>
                        <Typography variant="body2">Year Groups</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">
                          {yearGroups.reduce((total, yg) => total + yg.bands.length, 0)}
                        </Typography>
                        <Typography variant="body2">Bands</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">
                          {mockData.subjectSets.length}
                        </Typography>
                        <Typography variant="body2">Subject Sets</Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Next Steps
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Your curriculum plan is ready for timetable generation.
                  </Typography>
                  <Button variant="contained" fullWidth sx={{ mb: 1 }}>
                    Save Curriculum Plan
                  </Button>
                  <Button variant="outlined" fullWidth sx={{ mb: 1 }}>
                    Export Plan
                  </Button>
                  <Button variant="outlined" fullWidth>
                    Continue to School Structure
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Curriculum Planning
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Design and manage your complete curriculum structure, subject allocation, and student groupings
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent(activeStep)}

          <Box sx={{ display: 'flex', flexDirection: 'row', pt: 3 }}>
            <Button color="inherit" disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
              Back
            </Button>
            <Box sx={{ flex: '1 1 auto' }} />
            <Button onClick={handleNext} disabled={activeStep === steps.length - 1}>
              {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Add Subject Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Subject</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject Name"
                value={newSubject.subjectName || ''}
                onChange={(e) => setNewSubject({ ...newSubject, subjectName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Subject Code"
                value={newSubject.subjectCode || ''}
                onChange={(e) => setNewSubject({ ...newSubject, subjectCode: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={newSubject.department || ''}
                  onChange={(e) => setNewSubject({ ...newSubject, department: e.target.value })}
                >
                  <MenuItem value="Mathematics">Mathematics</MenuItem>
                  <MenuItem value="English">English</MenuItem>
                  <MenuItem value="Science">Science</MenuItem>
                  <MenuItem value="Humanities">Humanities</MenuItem>
                  <MenuItem value="Languages">Languages</MenuItem>
                  <MenuItem value="Arts">Arts</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Periods Per Week"
                type="number"
                value={newSubject.totalPeriodsPerWeek || ''}
                onChange={(e) => setNewSubject({ ...newSubject, totalPeriodsPerWeek: Number.parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Lesson Duration (mins)"
                type="number"
                value={newSubject.lessonDuration || ''}
                onChange={(e) => setNewSubject({ ...newSubject, lessonDuration: Number.parseInt(e.target.value) })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSubject}>
            Add Subject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Band Dialog */}
      <Dialog open={showBandDialog} onClose={() => setShowBandDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Band</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Band Name"
                value={newBand.name || ''}
                onChange={(e) => setNewBand({ ...newBand, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Band Type</InputLabel>
                <Select value={newBand.type || ''} onChange={(e) => setNewBand({ ...newBand, type: e.target.value })}>
                  <MenuItem value="ability">Ability-based</MenuItem>
                  <MenuItem value="mixed">Mixed ability</MenuItem>
                  <MenuItem value="non-ability">Non-ability</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Capacity"
                type="number"
                value={newBand.capacity || ''}
                onChange={(e) => setNewBand({ ...newBand, capacity: Number.parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={newBand.description || ''}
                onChange={(e) => setNewBand({ ...newBand, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBandDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddBand}>
            Add Band
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CurriculumPlanning;
